import { authFetch } from '../api/client';

const SEAWALL_API = import.meta.env.VITE_SEAWALL_API_URL ?? '/seawall';

export type ApiBox = {
  ID: number | string;
  locationID: number | string | null;
  nickname: string;
  public: number | boolean;
  assembleDateTime: string | null;
  deployDate: string | null;
};

export type ResolvedBox = {
  id: string;
  locationID: string;
  nickname: string;
  isPublic: boolean;
  reused: boolean;
};

export type NewBoxInput = {
  locationID: string;
  nickname: string;
  public: boolean;
};

function asBoxList(data: unknown): ApiBox[] {
  if (Array.isArray(data)) return data as ApiBox[];
  if (data && typeof data === 'object') {
    const record = data as { rows?: ApiBox[]; boxes?: ApiBox[] };
    if (Array.isArray(record.rows)) return record.rows;
    if (Array.isArray(record.boxes)) return record.boxes;
  }
  return [];
}

export function boxLocationId(box: ApiBox): string | null {
  if (box.locationID === null || box.locationID === undefined) return null;
  return String(box.locationID);
}

export function isPublicBox(box: ApiBox): boolean {
  return box.public === true || box.public === 1;
}

export function toResolvedBox(box: ApiBox, locationID: string, reused: boolean): ResolvedBox {
  return {
    id: String(box.ID),
    locationID,
    nickname: box.nickname,
    isPublic: isPublicBox(box),
    reused,
  };
}

export async function loadBoxes(): Promise<ApiBox[]> {
  const response = await fetch(`${SEAWALL_API}/boxes/`);
  if (!response.ok) {
    throw new Error(`Boxes API responded with ${response.status}`);
  }
  return asBoxList(await response.json());
}

export function boxesAtLocation(boxes: ApiBox[], locationID: string): ApiBox[] {
  return boxes.filter((box) => boxLocationId(box) === locationID);
}

async function parseApiError(res: Response): Promise<string> {
  if (res.status === 401 || res.status === 403) {
    return 'Your session expired or you do not have permission. Sign in again and retry.';
  }
  try {
    const data = await res.json();
    if (typeof data?.message === 'string') return data.message;
  } catch {
    /* empty body */
  }
  return `Request failed (${res.status})`;
}

export async function createBox(input: NewBoxInput): Promise<ResolvedBox> {
  const locationID = Number(input.locationID);
  if (!Number.isFinite(locationID)) {
    throw new Error(`Location ID "${input.locationID}" is not a valid numeric ID.`);
  }

  const res = await authFetch(`${SEAWALL_API}/boxes/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      locationID,
      nickname: input.nickname,
      public: input.public,
    }),
  });

  if (!res.ok) throw new Error(await parseApiError(res));

  const data = (await res.json()) as { id?: string | number };
  if (data.id === undefined || data.id === null) {
    throw new Error('Box was created but no ID was returned.');
  }

  return {
    id: String(data.id),
    locationID: input.locationID,
    nickname: input.nickname,
    isPublic: input.public,
    reused: false,
  };
}
